import { DATA_SERVICE_BASE_URL } from '../config/runtime';
import { fetchWithAuth } from './http';
import { LoginResponse } from './authApi';

export type OAuthProvider = 'google' | 'apple';
export type ProviderAvailability = Record<OAuthProvider, { enabled: boolean }>;
export interface ProviderLink {
  provider: OAuthProvider;
  email?: string | null;
  displayName?: string | null;
  linkedAt?: string;
}

async function readResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Social sign-in is unavailable. Please try again or use email and password.');
  }
  return data as T;
}

async function request<T>(path: string, body?: unknown, authenticated = false): Promise<T> {
  const options: RequestInit = {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
  const response = authenticated
    ? await fetchWithAuth(path, options)
    : await fetch(`${DATA_SERVICE_BASE_URL}${path}`, options);
  return readResponse<T>(response);
}

export async function getProviders(signal?: AbortSignal): Promise<ProviderAvailability> {
  return readResponse(await fetch(`${DATA_SERVICE_BASE_URL}/api/auth/providers?platform=web`, {
    credentials: 'include', cache: 'no-store', signal,
  }));
}

export function createOAuthTransaction(input: {
  provider: OAuthProvider;
  codeChallenge: string;
  intent: 'login' | 'link';
}): Promise<{ transactionId: string; nonce: string; authorizationUrl: string }> {
  return request('/api/auth/oauth/transactions', {
    ...input, platform: 'web', product: 'SCOUTCAST',
  }, input.intent === 'link');
}

export function exchangeOAuth(input: { transactionId: string; code: string; codeVerifier: string }): Promise<LoginResponse> {
  return request('/api/auth/oauth/exchange', input);
}

export function verifyOAuthMfa(tempToken: string, code: string): Promise<LoginResponse> {
  return request('/api/auth/oauth/mfa/verify', { tempToken, code, platform: 'web' });
}

export function getProviderLinks(): Promise<{ links: ProviderLink[] }> {
  return request('/api/auth/oauth/links', undefined, true);
}

export async function unlinkProvider(provider: OAuthProvider): Promise<void> {
  const response = await fetchWithAuth(`/api/auth/oauth/links/${provider}`, { method: 'DELETE' });
  if (!response.ok) await readResponse(response);
}

export async function logoutOAuth(): Promise<void> {
  await request('/api/auth/oauth/logout', {});
}
